import React, { useEffect, useState } from "react";
import { collection, query, orderBy, onSnapshot, updateDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(collection(db, "orders"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setOrders(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, []);

  // Function to generate 6-digit OTP
  const generateOTP = () => Math.floor(100000 + Math.random() * 900000);

  const handleStatusChange = async (id, newStatus) => {
    const orderRef = doc(db, "orders", id);
    let updateData = { status: newStatus };

    if (newStatus === "Out for Delivery") {
      const otp = generateOTP();
      updateData = { ...updateData, deliveryOTP: otp, otpVerified: false };
      alert(`OTP generated for Order ID ${id}: ${otp}`);
    }

    await updateDoc(orderRef, updateData);
  };

  const handleVerifyOTP = async (order) => {
    const userOTP = prompt("Enter the OTP received from the user:");
    if (!userOTP) return;

    if (parseInt(userOTP) === order.deliveryOTP) {
      const orderRef = doc(db, "orders", order.id);
      await updateDoc(orderRef, { otpVerified: true, status: "Delivered" });
      alert("OTP verified! Order marked as Delivered.");
    } else {
      alert("Incorrect OTP. Try again.");
    }
  };

  // Open Google Maps at the given latitude and longitude
  const handleTrackLocation = (location) => {
    if (!location?.latitude || !location?.longitude) {
      alert("Location not available for this order.");
      return;
    }
    const url = `https://www.google.com/maps?q=${location.latitude},${location.longitude}`;
    window.open(url, "_blank");
  };

  return (
    <div className="min-h-screen p-6 bg-gray-100">
      <h1 className="text-3xl font-bold mb-6">All Orders</h1>
      {orders.length === 0 && <p>No orders yet.</p>}

      <div className="space-y-6">
        {orders.map((order) => {
          const total = order.cartItems?.reduce((sum, item) => sum + item.price * item.quantity, 0);

          return (
            <div key={order.id} className="bg-white rounded-lg shadow p-6 border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-semibold">
                    Order ID: <span className="text-gray-600 font-normal">{order.id}</span>
                  </h2>
                  <p className="text-sm text-gray-500">
                    Placed At: {order.createdAt?.toDate ? order.createdAt.toDate().toLocaleString() : "N/A"}
                  </p>
                  {order.location && (
                    <p className="text-sm text-gray-500 mt-1">
                      Location: {order.location.address || `Lat: ${order.location.latitude}, Lng: ${order.location.longitude}`}
                    </p>
                  )}
                  {order.deliveryOTP && (
                    <p className="text-sm text-green-600 mt-1">
                      OTP: {order.deliveryOTP} {order.otpVerified ? "(Verified)" : "(Pending Verification)"}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <label className="font-medium mr-2">Status:</label>
                  <select
                    value={order.status}
                    onChange={(e) => handleStatusChange(order.id, e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1"
                  >
                    <option>Pending</option>
                    <option>Preparing</option>
                    <option>Out for Delivery</option>
                    <option disabled={!order.otpVerified}>Delivered</option>
                    <option>Cancelled</option>
                  </select>

                  {order.deliveryOTP && !order.otpVerified && order.status === "Out for Delivery" && (
                    <button
                      onClick={() => handleVerifyOTP(order)}
                      className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition"
                    >
                      Verify OTP
                    </button>
                  )}

                  {/* Track Order Button */}
                  {/* <button
                    onClick={() => navigate(`/order-tracking/${order.id}`)}
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                  >
                    Track Order
                  </button> */}

                  {/* Track Location Button */}
                  {order.location && (
                    <button
                      onClick={() => handleTrackLocation(order.location)}
                      className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
                    >
                      View on Map
                    </button>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p><strong>User:</strong> {order.userEmail}</p>
                  <p><strong>Payment Method:</strong> {order.paymentMethod}</p>
                  <p><strong>Total Amount:</strong> ₹{total ? total.toFixed(2) : "0.00"}</p>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Items:</h3>
                  <ul className="list-disc list-inside text-sm text-gray-700">
                    {order.cartItems?.map((item) => (
                      <li key={item.id}>
                        {item.name} × {item.quantity} — ₹{(item.price * item.quantity).toFixed(2)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminOrders;
